import type ProviderAccount from '~/types/ProviderAccount';

import { type Option } from '~/components/Select';
import { type TestTagType } from '~/components/Tag/TestTag/TestTag';
import { CANADA_POST, FEDEX, ROYAL_MAIL_SF } from '~/constants/carriers';
import { currencies, type Currency } from '~/constants/currency';
import { type TranslationFunction } from '~/utils/translate';

import type CarrierAccount from './CarrierAccount';

type SurchargeDetails = {
  copy: string;
  shouldTranslate: boolean;
  value: number;
};

export type Surcharges = {
  baseCharge: SurchargeDetails;
  currency: Maybe<Currency>;
  customsSurcharge?: SurchargeDetails;
  fuelSurcharge?: SurchargeDetails;
  otherSurcharges?: SurchargeDetails;
  residentialSurcharge?: SurchargeDetails;
  taxes?: SurchargeDetails;
};

type JSONServiceLevelTerms = {
  base_charge: number;
  base_price?: number;
  cost?: number;
  currency: string;
  customs_surcharge?: number;
  dropoff_surcharge?: number;
  fuel_surcharge?: number;
  other_surcharges?: number;
  residential_surcharge?: number;
  taxes?: number;
  vat?: number;
};

export type WebRatesServiceLevel = {
  extended_token: string;
  name: string;
  parent_servicelevel: string;
  terms: string;
  token: string;
};

type ServiceLevelTerms = Surcharges | string;

const getCurrency = (jsonServiceLevelTerms: JSONServiceLevelTerms) => {
  const parsedCurrency = Object.values(currencies).find(
    (currency) => currency.iso === jsonServiceLevelTerms.currency.toUpperCase(),
  );

  return parsedCurrency || currencies['USD'];
};

const parseFedExServiceLevel = (jsonServiceLevelTerms: JSONServiceLevelTerms) => {
  const terms: ServiceLevelTerms = {
    baseCharge: {
      copy: 'orders:calculateRates:rates:surcharges:baseCharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.base_charge),
    },
    currency: getCurrency(jsonServiceLevelTerms),
  };
  if (jsonServiceLevelTerms.fuel_surcharge) {
    terms.fuelSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:fuelSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.fuel_surcharge),
    } as SurchargeDetails;
  }

  if (jsonServiceLevelTerms.residential_surcharge) {
    terms.residentialSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:residentialSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.residential_surcharge),
    };
  }

  if (jsonServiceLevelTerms.customs_surcharge) {
    terms.customsSurcharge = {
      copy: 'orders:calculateRates:rates:surcharges:customsSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.customs_surcharge),
    };
  }

  if (jsonServiceLevelTerms.taxes) {
    terms.taxes = {
      copy: 'orders:calculateRates:rates:surcharges:taxes',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.taxes),
    };
  }
  if (jsonServiceLevelTerms.other_surcharges) {
    terms.otherSurcharges = {
      copy: 'orders:calculateRates:rates:surcharges:otherSurcharges',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.other_surcharges),
    };
  }

  return terms;
};

const parseCanadaPostServiceLevel = (jsonServiceLevelTerms: JSONServiceLevelTerms) => {
  const parsedTerms = Object.entries(jsonServiceLevelTerms);
  const terms: ServiceLevelTerms = Object.entries(parsedTerms).reduce(
    (termsObject, [, [name, value]]) => {
      if (name === 'currency') {
        termsObject.currency = getCurrency(jsonServiceLevelTerms);
      } else {
        (termsObject as Record<string, unknown>)[name] = {
          copy: name,
          shouldTranslate: false,
          value: Number(value),
        };
      }
      return termsObject;
    },
    {} as Surcharges,
  );

  return terms as ServiceLevelTerms;
};

const parseRoyalMailSFServiceLevel = (
  jsonServiceLevelTerms: JSONServiceLevelTerms,
): ServiceLevelTerms => {
  const terms: Surcharges = {
    baseCharge: {
      copy: 'orders:calculateRates:rates:surcharges:baseCharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.base_price),
    },
    currency: getCurrency(jsonServiceLevelTerms),
  };

  if (jsonServiceLevelTerms.dropoff_surcharge) {
    terms.otherSurcharges = {
      copy: 'orders:calculateRates:rates:surcharges:dropoffSurcharge',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.dropoff_surcharge),
    };
  }

  if (jsonServiceLevelTerms.vat !== undefined && jsonServiceLevelTerms.vat !== null) {
    terms.taxes = {
      copy: 'orders:calculateRates:rates:surcharges:vat',
      shouldTranslate: true,
      value: Number(jsonServiceLevelTerms.vat),
    };
  }

  return terms;
};

const fromJSONServiceLevelTerms = (
  serviceLevelTerms: string,
  provider: string,
): ServiceLevelTerms => {
  let terms: ServiceLevelTerms = '';
  try {
    const jsonServiceLevelTerms: JSONServiceLevelTerms = JSON.parse(serviceLevelTerms);
    if (provider === FEDEX.name) terms = parseFedExServiceLevel(jsonServiceLevelTerms);

    if (provider === CANADA_POST.name) terms = parseCanadaPostServiceLevel(jsonServiceLevelTerms);

    if (provider.startsWith(ROYAL_MAIL_SF.name))
      terms = parseRoyalMailSFServiceLevel(jsonServiceLevelTerms);
  } catch (e) {
    terms = serviceLevelTerms;
  }

  return terms;
};

type JSONServiceLevel = {
  extended_token?: string;
  name: string;
  parent_servicelevel?: JSONServiceLevel;
  terms: string;
  token: string;
};
type ServiceLevel = {
  account_object_id?: string;
  badge?: string;
  extended_token?: string;
  hoverText?: string;
  is_hidden?: boolean;
  is_used?: boolean;
  name: string;
  parent_servicelevel?: ServiceLevel;
  provider_image_75?: string;
  provider_image_200?: string;
  provider_name?: string;
  supports_return_labels?: boolean;
  terms?: ServiceLevelTerms;
  token: string;
};

type MappedServiceLevelsToken = { [token: string]: ServiceLevel[] };

// Copying from ember.
// For some reason we ignore some service levels
// Just doing this to have feature parity
// Some of this have duplicated tokens as well
// FIXME: later pls

const IGNORED_SERVICE_LEVELS_TOKENS = [
  'hermes_uk_home_delivery',
  'hermes_uk_parcelshop',
  'usps_media_mail',
  'usps_library_mail',
];
const IGNORED_SERVICE_LEVELS_NAMES = ['FC Package', 'FC Mail'];

const ONERATE = 'ONERATE';

const SERVICE_LEVEL_BEST: ServiceLevel = { name: 'Best rate', token: 'best' };
const SERVICE_LEVEL_CHEAPEST: ServiceLevel = { name: 'Cheapest rate', token: 'cheapest' };
const SERVICE_LEVEL_FASTEST: ServiceLevel = { name: 'Fastest rate', token: 'fastest' };

const ServiceLevelCategories: ServiceLevel[] = [
  SERVICE_LEVEL_BEST,
  SERVICE_LEVEL_CHEAPEST,
  SERVICE_LEVEL_FASTEST,
];
const ServiceLevelCategoryByToken: HashMap<string, ServiceLevel> = ServiceLevelCategories.reduce(
  (map, category) => Object.assign(map, { [category.token]: category }),
  {},
);

const SHIPPING_OPTIONS_SERVICE_LEVELS: ServiceLevel[] = [
  {
    hoverText:
      'A balance of cost and speed using reliable carrier services like UPS Ground or USPS Priority Mail.',
    name: 'Best',
    token: 'best',
  },
  { name: 'Cheapest', token: 'cheapest' },
  { name: 'Fastest', token: 'fastest' },
];

// Currently removed fastest since EDD causes the display of fastest to sometimes be incorrect
const SHIPPING_OPTIONS_FOR_DEFAULTS = SHIPPING_OPTIONS_SERVICE_LEVELS.filter(
  (option) => option.token !== 'fastest',
);

const toOptions = (serviceLevels: ServiceLevel[], isReturn: boolean): Option<string>[] => {
  const filteredServiceLevels = isReturn
    ? serviceLevels
    : serviceLevels.filter(
        (serviceLevel) =>
          !IGNORED_SERVICE_LEVELS_TOKENS.includes(serviceLevel.token) &&
          !IGNORED_SERVICE_LEVELS_NAMES.includes(serviceLevel.name),
      );

  return filteredServiceLevels.map((serviceLevel) => ({
    label: serviceLevel.name,
    value: `${serviceLevel.token}`, // token could be repeated between services levels :/
  }));
};

const isOneRate = (serviceLevel: ServiceLevel): boolean =>
  !!serviceLevel && !!serviceLevel.terms && serviceLevel.terms === ONERATE;

export const fromWebRatesServiceLevel = (
  webRatesServiceLevel: WebRatesServiceLevel,
): ServiceLevel => ({
  extended_token: webRatesServiceLevel.extended_token,
  name: webRatesServiceLevel.name,
  terms: webRatesServiceLevel.terms || '',
  token: webRatesServiceLevel.token,
});

const addServiceLevelAccountType = (providerAccount: ProviderAccount, t: TranslationFunction) => {
  let providerDescription = '';
  if (providerAccount.isShippoAccount)
    providerDescription = `(${t('automations.form.shippoMasterAccount')})`;
  else if (providerAccount.nickname) providerDescription = `(${providerAccount.nickname})`;

  if (providerAccount.isTest) {
    if (providerDescription.length > 0) providerDescription += ' ';

    providerDescription += `- ${t('automations.form.testAccount')}`;
  }
  return providerDescription;
};

const formatOptions = ({
  defaultServiceLevel,
  options,
  t,
}: {
  defaultServiceLevel: Maybe<string>;
  options: ProviderAccount[];
  t: TranslationFunction;
}) =>
  options
    .sort((a: ProviderAccount, b: ProviderAccount) => {
      if (!a.providerName || !b.providerName) return 0;

      return a.providerName.localeCompare(b.providerName);
    })
    .map((providerAccount: ProviderAccount) => {
      const { visibleServiceLevels } = providerAccount;
      const serviceLevelOptions = toOptions(visibleServiceLevels, false).map(
        ({ label, value }) => ({
          label:
            label.toString() +
            (value === defaultServiceLevel
              ? ` (${t('automations.form.defaultServiceLevelOption')})`
              : ''),
          value: providerAccount.objectId + '|' + value,
        }),
      );

      const providerDescription = addServiceLevelAccountType(providerAccount, t);

      return {
        label: `${providerAccount.providerName} ${providerDescription}`,
        options: serviceLevelOptions,
      };
    });

// get Service Level Account by token and accountId, if !accountId, get the first account that return from matched token.
const getServiceLevelAccountByToken = ({
  accountId,
  mappedServiceLevels,
  token,
}: {
  accountId: Maybe<string>;
  mappedServiceLevels: MappedServiceLevelsToken;
  token?: string;
}): ServiceLevel | undefined => {
  if (Object.keys(mappedServiceLevels).length > 0 && token) {
    const serviceLevelAccounts = mappedServiceLevels?.[token];
    if (serviceLevelAccounts?.length > 0) {
      // Check if a Service Level exists with both the token and specified Account
      if (accountId) {
        const serviceLevelWithMatchingAccount = serviceLevelAccounts.find(
          (serviceLevel) => serviceLevel.account_object_id === accountId,
        );
        if (serviceLevelWithMatchingAccount) return serviceLevelWithMatchingAccount;
      }
      // otherwise, simply return the first Account with a matching token
      return serviceLevelAccounts[0];
    }
  }
  return undefined;
};

const serviceLevelMatchesToken = (
  serviceLevel: ServiceLevel,
  serviceLevelToken: string,
): boolean => {
  if (!serviceLevel || !serviceLevelToken) return false;
  // check if an extended token is available for more precise comparisons
  else if (serviceLevel.extended_token && serviceLevel.extended_token === serviceLevelToken)
    return true;
  // otherwise, check if the tokens match
  else if (serviceLevel.token === serviceLevelToken) return true;

  // finally, if the tokens didn't match, try matching the token of the parent (if a parent exists)
  return (
    !!serviceLevel.parent_servicelevel &&
    serviceLevelMatchesToken(serviceLevel.parent_servicelevel, serviceLevelToken)
  );
};

const getProviderAccountByObjectId = (
  accounts: Maybe<ProviderAccount[]>,
  objectId?: number | string,
) => {
  if (!accounts || accounts.length === 0 || !objectId) return;
  return accounts.find((account) => account.objectId === objectId);
};

export type ServiceLevelData = {
  amount?: number;
  carrierAccount?: CarrierAccount; // Carrier Account Object for the selected Rate
  count: number; // 5
  currency?: string;
  estimatedDays?: number; // 7
  isTrackable?: boolean; // whether the package is trackable or no
  name: string; // 'USPS Priority Mail'
  provider: string; // 'USPS'
  testMode?: TestTagType;
  token: string;
};

export type BatchServiceLevelData = {
  // orders count
  count: number;
  data: {
    [token: string]: ServiceLevelData;
  };
};

export type AvailableServiceLevels = {
  domestic: BatchServiceLevelData;
  international: BatchServiceLevelData;
};

export default ServiceLevel;

export {
  JSONServiceLevel,
  MappedServiceLevelsToken,
  SHIPPING_OPTIONS_FOR_DEFAULTS,
  SHIPPING_OPTIONS_SERVICE_LEVELS,
  ServiceLevelCategories,
  ServiceLevelCategoryByToken,
  ServiceLevelTerms,
  SurchargeDetails,
  formatOptions,
  fromJSONServiceLevelTerms,
  getProviderAccountByObjectId,
  getServiceLevelAccountByToken,
  isOneRate,
  serviceLevelMatchesToken,
  toOptions,
};
